import type { ConvexReactClient } from "convex/react";
import { useConvex, useMutation } from "convex/react";
import { ArrowLeft } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import { ReviewCard } from "@/components/Review/ReviewCard";
import { ReviewComplete } from "@/components/Review/ReviewComplete";
import { LazySpinner } from "@/components/Spinner";
import { getClientDay } from "@/helpers/getClientDay";
import { useReviewTime } from "@/hooks/useReviewTime";
import { api } from "@convex/_generated/api";
import type { Doc, Id } from "@convex/_generated/dataModel";

interface ReviewCardsProps {
  maybeDeckId?: string;
  mode: "all" | "scheduled" | "missed";
}

const modeToQueryFnMap: Record<
  ReviewCardsProps["mode"],
  (client: ConvexReactClient, deckId: Id<"decks">, reviewTime: number) => Promise<Doc<"cards">[]>
> = {
  scheduled: (client, deckId, reviewTime) =>
    client.query(api.reviews.getCardsToReview, { deckId, reviewTime }),
  all: (client, deckId) => client.query(api.cards.getByDeck, { deckId }),
  missed: (client, deckId) => client.query(api.reviews.getRecentlyMissedCards, { deckId }),
};

export const ReviewCards = ({ maybeDeckId, mode }: ReviewCardsProps) => {
  // We use a one-off convexClient.query function instead of useQuery, because we want the list of allReviewCards
  // to be stable for a given review, even as we save the review results back to the server (which would alter the contents of the queries done later).
  const [allReviewCards, setAllReviewCards] = useState<Doc<"cards">[] | undefined>();
  const client = useConvex();
  const reviewTime = useReviewTime();
  useEffect(() => {
    const updateReviewCards = async () => {
      const queryFn = modeToQueryFnMap[mode];
      const cards = await queryFn(client, maybeDeckId as Id<"decks">, reviewTime);
      setAllReviewCards(cards);
    };
    void updateReviewCards();
  }, [client, maybeDeckId, mode, reviewTime]);

  // We want to choose a random, stable ordering for the cards to review.
  const randomizedReviewCards = useMemo(
    () => allReviewCards?.sort(() => Math.random() - 0.5),
    [allReviewCards]
  );

  // Keep track of the ids of cards that have already been reviewed.
  const [reviewedCardIds, setReviewedCardIds] = useState<Id<"cards">[]>([]);

  const remainingCards = randomizedReviewCards?.filter(
    (card) => !reviewedCardIds.includes(card._id)
  );
  const totalCards = allReviewCards?.length ?? 0;
  const currentCardNumber = reviewedCardIds.length;
  const currentCard = remainingCards?.[0];
  const progress = (reviewedCardIds.length / totalCards) * 100;

  // Mutation to save review results.

  const saveReview = useMutation(api.reviews.saveReview);
  const handleResponse = (remembered: boolean) => {
    if (currentCard) {
      setReviewedCardIds((prev) => [...prev, currentCard._id]);
      void saveReview({
        cardId: currentCard._id,
        remembered,
        reviewDay: getClientDay(),
      });
    }
  };

  // Keep track of review start time.
  const reviewStartTime = useMemo(() => Date.now(), []);

  const goBackPath = maybeDeckId ? `/decks/${maybeDeckId}` : "/";
  const loading = !allReviewCards;
  const hasCardsToReview =
    reviewedCardIds.length > 0 || (allReviewCards && allReviewCards.length > 0);

  return (
    <div className="h-dvh w-dvw overflow-hidden bg-gray-100 flex flex-col">
      <header className="p-4 bg-white border-b">
        <div className="container mx-auto flex items-center">
          <Link to={goBackPath} className="flex items-center text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5 mr-2" />
            <span>Back</span>
          </Link>
        </div>
      </header>
      <div className="container mx-auto p-4 flex-1 flex flex-col">
        {hasCardsToReview && (
          <div className="mb-6">
            <div className="relative h-1 bg-gray-200 rounded-full">
              <motion.div
                className="absolute h-1 bg-blue-500 rounded-full"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute h-3 w-3 bg-blue-500 rounded-full top-1/2 transform -translate-y-1/2"
                animate={{ left: `${progress}%` }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              />
            </div>
            <div className="text-center text-sm text-gray-600 mt-2">
              {currentCardNumber === totalCards
                ? "\u00A0"
                : `${currentCardNumber + 1} of ${totalCards}`}
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {loading ? (
            <div className="flex-1 flex items-center justify-center h-full">
              <LazySpinner />
            </div>
          ) : !hasCardsToReview ? (
            <div className="text-center mt-15">No cards to review</div>
          ) : currentCard ? (
            <ReviewCard
              currentCard={currentCard}
              handleResponse={handleResponse}
              key={currentCard._id}
            />
          ) : (
            <ReviewComplete
              goBackPath={goBackPath}
              reviewedCardsCount={reviewedCardIds.length}
              reviewStartTime={reviewStartTime}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
